import { animated, useTransition } from '@react-spring/web';
import { type FC, useEffect, useRef, useState } from 'react';

interface RippleItem {
  x: number;
  y: number;
  size: number;
  key: number;
}

export const Ripple: FC = () => {
  const [ripples, setRipples] = useState<RippleItem[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current?.parentElement;
    if (!container) {
      return;
    }

    const handlePointerDown = (e: PointerEvent) => {
      const target = e.currentTarget as HTMLElement;
      if (
        target.getAttribute('disabled') !== null ||
        target.classList.contains('disabled') ||
        (target as HTMLButtonElement).disabled
      ) {
        return;
      }

      const rect = container.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height) * 2;
      const x = e.clientX - rect.left - size / 2;
      const y = e.clientY - rect.top - size / 2;

      const newRipple = { x, y, size, key: Date.now() + Math.random() };

      setRipples((prev) => [...prev.slice(-4), newRipple]);
    };

    container.addEventListener('pointerdown', handlePointerDown);

    return () => {
      container.removeEventListener('pointerdown', handlePointerDown);
    };
  }, []);

  const transitions = useTransition(ripples, {
    from: { opacity: 0.35, transform: 'scale(0)' },
    enter: { opacity: 0, transform: 'scale(0.45)' },
    leave: { opacity: 0 },
    config: { duration: 500 },
    onRest: (_result, _ctrl, item) => {
      if (item) {
        setRipples((prev) => prev.filter((i) => i.key !== item.key));
      }
    },
  });

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 pointer-events-none overflow-hidden rounded-[inherit]"
      style={{ zIndex: 0 }}
    >
      {transitions((style, item) => (
        <animated.span
          className="absolute bg-current rounded-full pointer-events-none will-change-transform"
          style={{
            ...style,
            left: item.x,
            top: item.y,
            width: item.size,
            height: item.size,
          }}
        />
      ))}
    </div>
  );
};
